/**
 * FoodPulseHomeV3 — rebuilt Feb 2026 to exactly match the design mockup
 * approved for iter160. Prior variants (three-colour serif hero, deal
 * rail, journey-section stack) have been retired in favour of this
 * cleaner marketing gateway.
 *
 * Layout stack:
 *   1. FoodPulseTopNav (existing — sticky location picker, search, auth).
 *   2. Hero — big black headline "Discover. Share. Feed. / Reduce Food
 *      Waste.", two search inputs (place + query), one green CTA, three
 *      text links, and a right-side 4-panel photo collage with cursive
 *      annotations.
 *   3. Six-tab pill strip (For You / Near You / Fresh Deals / Recipes /
 *      Donate Food / Community). Client-side switch; each tab reveals a
 *      different Fresh Near You slice.
 *   4. Fresh Near You — 4 large photo cards (Today's Local Deals,
 *      Farmers & Markets, Share Surplus Food, Urban Harvest).
 *   5. Explore FoodPulse — 6 icon feature chips linking into the
 *      specialised tools.
 *   6. Simple footer strip with logo + policy links.
 *
 * All right-side photography reuses the GPT-Image-1 hero images we
 * already generated for the cornerstone guides (10 images, served via
 * /api/food-pulse/guides/:slug/hero.png). This keeps the visual
 * identity consistent across the site with zero extra image spend.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, MapPin, Search, Leaf, ScanLine, ShoppingBasket, HandHeart,
  Recycle, Heart, Utensils, Package, Download, X, Smartphone, CheckCircle2,
  BookOpen,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import FoodPulseTopNav from "@/components/FoodPulseTopNav";
import JsonLd, {
  foodPulseOrganizationSchema,
  foodPulseWebsiteSchema,
} from "@/components/JsonLd";

const FP_OG_IMAGE    = "/api/og/host-default.png";
const SPLINK_SHORT_URL = "https://splink.pro/food-app";
const SPLINK_QR_PNG    = "https://splink.pro/api/qr/food-app?fmt=png";

// Reliable Unsplash stock photos — replacing /api guide heroes which may
// not have been AI-generated yet (they return JSON 404 when missing).
const IMG_FARMER    = "https://images.unsplash.com/photo-1788538812851-9aacd4189fa8?w=900&q=80";  // community garden
const IMG_MARKET    = "https://images.unsplash.com/photo-1485637701894-09ad422f6de6?w=900&q=80";  // tomatoes at market
const IMG_COMMUNITY = "https://images.unsplash.com/photo-1557844352-761f2565b576?w=900&q=80";    // farm produce
const IMG_SHARING   = "https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=900&q=80"; // raised beds

const IMG_DEALS    = "https://images.unsplash.com/photo-1567306295427-94503f8300d7?w=800&q=80";  // farmer's market basket
const IMG_FARMERS  = "https://images.unsplash.com/photo-1591586116988-62fe65164f8d?w=800&q=80";  // market stall
const IMG_SURPLUS  = "https://images.pexels.com/photos/33747001/pexels-photo-33747001.jpeg?auto=compress&w=800"; // hands with tomatoes
const IMG_HARVEST  = "https://images.unsplash.com/photo-1598512752423-099d75d4865c?w=800&q=80";  // community harvest

// The primary CTA rail — six one-word journeys, each links into a real
// FoodPulse tool. Order lifted verbatim from the design brief.
const TABS = [
  { key: "for-you",     label: "For You",     to: "/food-pulse/deals?scope=personal" },
  { key: "near-you",    label: "Near You",    to: "/food-pulse/deals" },
  { key: "fresh-deals", label: "Fresh Deals", to: "/food-pulse/deals?fresh=1" },
  { key: "recipes",     label: "Recipes",     to: "/food-pulse/recipes" },
  { key: "donate-food", label: "Donate Food", to: "/food-pulse/donate" },
  { key: "community",   label: "Community",   to: "/food-pulse/community" },
];

// Feature status labels per audit — "Available now", "Limited beta", "Growing database"
const FEATURES = [
  { key: "whats-in-food", label: "What's in Your Food?", sub: "Clear, trusted information.", to: "/food-pulse/food",           Icon: Leaf,           tint: "bg-emerald-50 text-emerald-800 ring-emerald-200", status: "live" },
  { key: "label-scanner", label: "Label Scanner",         sub: "Scan and learn instantly.",  to: "/food-pulse/label-scan",     Icon: ScanLine,       tint: "bg-violet-50 text-violet-800 ring-violet-200",   status: "beta" },
  { key: "products",      label: "Product Database",      sub: "Explore food products.",     to: "/food-pulse/products",       Icon: Package,        tint: "bg-amber-50 text-amber-800 ring-amber-200",    status: "growing" },
  { key: "recipes",       label: "Recipes & Nutrition",   sub: "Real food for real life.",   to: "/food-pulse/recipes",        Icon: Utensils,       tint: "bg-rose-50 text-rose-800 ring-rose-200",       status: "live" },
  { key: "charities",     label: "Food Charities",        sub: "Support local food programs.", to: "/food-pulse/charities",   Icon: Heart,          tint: "bg-teal-50 text-teal-800 ring-teal-200",       status: "live" },
  { key: "reduce-waste",  label: "Reduce Food Waste",     sub: "Simple actions. Big impact.", to: "/food-pulse/guides/food-waste-at-home", Icon: Recycle, tint: "bg-lime-50 text-lime-800 ring-lime-200",  status: "live" },
];

const STATUS_LABELS = {
  live:    { label: "Available now",        cls: "bg-emerald-100 text-emerald-800" },
  beta:    { label: "Limited beta",         cls: "bg-yellow-100 text-yellow-800" },
  growing: { label: "Growing database",     cls: "bg-blue-100 text-blue-800" },
  planned: { label: "Planned",              cls: "bg-slate-100 text-slate-600" },
};

const FRESH_CARDS = [
  {
    key: "deals",   to: "/food-pulse/deals",         img: IMG_DEALS,
    title: "Today's Local Deals",
    sub:   "Fresh food, great prices, healthier communities.",
  },
  {
    key: "farmers", to: "/food-pulse/businesses",    img: IMG_FARMERS,
    title: "Farmers & Markets",
    sub:   "Meet local growers and find nearby markets.",
  },
  {
    key: "surplus", to: "/food-pulse/donate",        img: IMG_SURPLUS,
    title: "Share Surplus Food",
    sub:   "Connect, share and keep good food in use.",
  },
  {
    key: "harvest", to: "/food-pulse/urban-harvest", img: IMG_HARVEST,
    title: "Urban Harvest",
    sub:   "Grow food. Stronger, greener neighborhoods.",
  },
];

export default function FoodPulseHomeV3() {
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [placeInput, setPlaceInput] = useState("");
  const [q, setQ] = useState("");
  const [guides, setGuides] = useState([]);

  // Fetch cornerstone guides for the knowledge section
  useEffect(() => {
    http.get("/food-pulse/guides").then(r => {
      const items = r.data?.items || [];
      setGuides(items.slice(0, 4));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const readLoc = () => {
      try {
        const raw = localStorage.getItem("fp_user_location") || localStorage.getItem("fp:userLocation");
        return raw ? JSON.parse(raw) : null;
      } catch { return null; }
    };
    const loc = readLoc();
    setLocation(loc);
    if (loc?.city) {
      setPlaceInput(loc.state ? `${loc.city}, ${loc.state}` : loc.city);
    }
    const onChange = () => {
      const l = readLoc();
      setLocation(l);
      if (l?.city) setPlaceInput(l.state ? `${l.city}, ${l.state}` : l.city);
    };
    window.addEventListener("fp:location:change", onChange);
    return () => window.removeEventListener("fp:location:change", onChange);
  }, []);

  const localityLabel = useMemo(() => {
    if (!location?.city) return "your area";
    return location.state ? `${location.city}, ${location.state}` : location.city;
  }, [location]);

  const submitExplore = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (placeInput.trim()) params.set("place", placeInput.trim());
    if (q.trim()) params.set("q", q.trim());
    navigate(`/food-pulse/deals?${params.toString()}`);
  };

  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem("fp_install_banner_dismissed") === "1"
  );

  const dismissBanner = () => {
    sessionStorage.setItem("fp_install_banner_dismissed", "1");
    setBannerDismissed(true);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="fp-home-v3">
      <SeoHead
        title="FoodPulse — Discover. Share. Feed. Reduce Food Waste."
        description={`Find fresh food, local deals, trusted information and ways to share surplus food in ${localityLabel}.`}
        image={FP_OG_IMAGE}
      />
      <JsonLd data={[foodPulseOrganizationSchema(), foodPulseWebsiteSchema()]} />

      <FoodPulseTopNav />

      {/* ─────────────────────────────────────────────────────────
          INSTALL STRIP — splink.pro QR + short URL, dismissible
         ───────────────────────────────────────────────────────── */}
      {!bannerDismissed && (
        <div
          className="relative w-full"
          style={{ background: "linear-gradient(100deg, #0f3d22 0%, #1A5C38 45%, #145c35 100%)" }}
          data-testid="fp-install-banner"
        >
          <div className="mx-auto flex max-w-7xl items-center gap-5 px-4 py-4 sm:py-5">

            {/* SpLink QR code — public image endpoint, no auth */}
            <div className="hidden sm:flex shrink-0 flex-col items-center gap-1.5">
              <a href={SPLINK_SHORT_URL} target="_blank" rel="noopener noreferrer">
                <div className="rounded-xl bg-white p-2 shadow-lg ring-2 ring-emerald-400/30 hover:ring-emerald-300 transition">
                  <img
                    src={SPLINK_QR_PNG}
                    alt="Scan to install FoodPulse"
                    width={80}
                    height={80}
                    className="block"
                    data-testid="fp-install-qr-img"
                  />
                </div>
              </a>
              {/* Short URL display */}
              <a
                href={SPLINK_SHORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-mono font-semibold text-emerald-300 hover:text-white transition tracking-wide"
                data-testid="fp-install-short-url"
              >
                splink.pro/food-app
              </a>
            </div>

            {/* Vertical divider */}
            <div className="hidden sm:block h-16 w-px bg-white/20 shrink-0" />

            {/* Copy */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Smartphone className="h-4 w-4 text-emerald-300 shrink-0" />
                <span className="text-white font-bold text-base sm:text-lg leading-snug">
                  Get FoodPulse on your phone — free, no app store needed
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                {["Works offline", "Auto-updates", "iOS & Android"].map(f => (
                  <span key={f} className="flex items-center gap-1 text-xs text-emerald-200">
                    <CheckCircle2 className="h-3 w-3" />{f}
                  </span>
                ))}
              </div>
              {/* Short URL on mobile */}
              <div className="mt-2 sm:hidden">
                <a
                  href={SPLINK_SHORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono font-semibold text-emerald-300 hover:text-white transition"
                >
                  → splink.pro/food-app
                </a>
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/install"
              data-testid="fp-install-banner-cta"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-black text-emerald-800 shadow-lg hover:bg-emerald-50 transition active:scale-95"
            >
              <Download className="h-4 w-4" />
              Install App
            </Link>

            {/* Dismiss */}
            <button
              onClick={dismissBanner}
              aria-label="Dismiss"
              data-testid="fp-install-banner-dismiss"
              className="absolute right-3 top-3 rounded-full p-1.5 text-white/50 hover:text-white hover:bg-white/10 transition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────
          HERO — headline + two search inputs + collage
         ───────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden border-b border-emerald-100"
        style={{
          background:
            "linear-gradient(180deg, #F4FBF0 0%, #FCFFF9 60%, #FFFFFF 100%)",
        }}
        data-testid="fp-home-hero"
      >
        {/* Decorative leaf */}
        <Leaf
          aria-hidden
          className="pointer-events-none absolute -left-6 top-8 h-32 w-32 rotate-[-18deg] text-emerald-600/10"
        />

        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 py-10 lg:grid-cols-[1.05fr_1fr] lg:gap-10 lg:py-14">
          {/* Left — headline + search + CTA */}
          <div>
            <h1
              className="font-serif text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
              data-testid="fp-hero-title"
            >
              Discover. Share. Feed.
              <br />
              <span className="text-slate-900">Reduce Food Waste.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
              Find fresh food, local deals, trusted information and ways to
              share surplus food in your community.
            </p>

            <form
              onSubmit={submitExplore}
              className="mt-6 flex flex-col gap-3"
              data-testid="fp-hero-search-form"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-700" />
                  <input
                    type="text"
                    value={placeInput}
                    onChange={(e) => setPlaceInput(e.target.value)}
                    placeholder="Choose a Place"
                    data-testid="fp-hero-place"
                    aria-label="Choose a place"
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm shadow-sm outline-none ring-emerald-500 focus:border-emerald-500 focus:ring-2"
                  />
                </div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-700" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="What are you looking for?"
                    data-testid="fp-hero-query"
                    aria-label="What are you looking for"
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm shadow-sm outline-none ring-emerald-500 focus:border-emerald-500 focus:ring-2"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="fp-hero-cta"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 text-base font-semibold text-white shadow-md transition hover:bg-emerald-700"
              >
                Explore Food Near You <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <Link
                to="/food-pulse/label-scan"
                data-testid="fp-hero-link-scan"
                className="font-semibold text-emerald-700 underline underline-offset-4 hover:text-emerald-800"
              >
                Scan a Label
              </Link>
              <span className="text-emerald-300">•</span>
              <Link
                to="/food-pulse/donate"
                data-testid="fp-hero-link-donate"
                className="font-semibold text-emerald-700 underline underline-offset-4 hover:text-emerald-800"
              >
                Donate Food
              </Link>
              <span className="text-emerald-300">•</span>
              <Link
                to="/food-pulse/urban-harvest"
                data-testid="fp-hero-link-surplus"
                className="font-semibold text-emerald-700 underline underline-offset-4 hover:text-emerald-800"
              >
                Offer Surplus Produce
              </Link>
            </div>
          </div>

          {/* Right — 4-panel photo collage with cursive annotations */}
          <HeroCollage />
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          NAVIGATION LINKS — simple inline pills, scroll with page
         ───────────────────────────────────────────────────────── */}
      <div
        className="mx-auto max-w-7xl px-4 pt-4 pb-2"
        data-testid="fp-home-tabs"
      >
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <Link
              key={t.key}
              to={t.to}
              data-testid={`fp-home-tab-${t.key}`}
              className="shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 transition"
            >
              {t.label}
            </Link>
          ))}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────
          FRESH NEAR YOU — 4 photo cards
         ───────────────────────────────────────────────────────── */}
      <section
        className="mx-auto max-w-7xl px-4 py-10"
        data-testid="fp-home-fresh"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-serif text-2xl font-bold text-slate-900 sm:text-3xl">
              Fresh Near You
            </h2>
            <span
              data-testid="fp-fresh-locality"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200"
            >
              <MapPin className="h-3 w-3" /> {localityLabel === "your area" ? "Set your location" : localityLabel}
            </span>
          </div>
          <Link
            to="/food-pulse/deals"
            data-testid="fp-fresh-see-more"
            className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline"
          >
            See more near you <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FRESH_CARDS.map((c) => (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`fp-fresh-card-${c.key}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="relative h-40 w-full overflow-hidden bg-slate-100">
                <img
                  src={c.img}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif text-lg font-semibold leading-tight text-slate-900">
                    {c.title}
                  </h3>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-emerald-600 transition-transform group-hover:translate-x-0.5" />
                </div>
                <p className="mt-1 text-sm text-slate-600">{c.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          TRUSTED GUIDES — cornerstone editorial content
         ───────────────────────────────────────────────────────── */}
      {guides.length > 0 && (
        <section className="border-t border-slate-100 bg-emerald-50/30" data-testid="fp-home-guides">
          <div className="mx-auto max-w-7xl px-4 py-10">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <h2 className="font-serif text-2xl font-bold text-slate-900 sm:text-3xl">
                  Trusted Food Knowledge
                </h2>
                <p className="mt-1 text-sm text-slate-500">Expert-reviewed guides, updated regularly.</p>
              </div>
              <Link to="/food-pulse/guides" data-testid="fp-guides-see-all"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline">
                All guides <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {guides.map(g => (
                <Link key={g.slug} to={`/food-pulse/guides/${g.slug}`}
                      data-testid={`fp-guide-card-${g.slug}`}
                      className="group flex flex-col rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                  <BookOpen className="h-6 w-6 text-emerald-600 mb-3" />
                  <h3 className="font-serif text-base font-semibold leading-tight text-slate-900 mb-1">
                    {g.title}
                  </h3>
                  <p className="text-xs text-slate-500 flex-1 line-clamp-2">{g.summary}</p>
                  {g.last_reviewed && (
                    <span className="mt-3 text-[10px] text-emerald-700 font-medium">
                      Reviewed {g.last_reviewed}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────────────────────────────────────
          EXPLORE FOODPULSE — 6-feature chip strip
         ───────────────────────────────────────────────────────── */}
      <section
        className="border-t border-slate-100 bg-slate-50/40"
        data-testid="fp-home-explore"
      >
        <div className="mx-auto max-w-7xl px-4 py-10">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-serif text-2xl font-bold text-slate-900 sm:text-3xl">
              Explore FoodPulse
            </h2>
            <Link
              to="/food-pulse/tour"
              data-testid="fp-explore-see-all"
              className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline"
            >
              See all features <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {FEATURES.map((f) => {
              const st = STATUS_LABELS[f.status];
              return (
                <Link
                  key={f.key}
                  to={f.to}
                  data-testid={`fp-explore-${f.key}`}
                  className={`group flex flex-col items-start gap-1 rounded-2xl border p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${f.tint} ring-1`}
                >
                  <span className="grid h-10 w-10 place-content-center rounded-xl bg-white/90">
                    <f.Icon className="h-5 w-5" />
                  </span>
                  <span className="mt-2 text-sm font-semibold leading-tight">{f.label}</span>
                  <span className="text-[11px] leading-snug opacity-80">{f.sub}</span>
                  {st && f.status !== "live" && (
                    <span className={`mt-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${st.cls}`}
                          data-testid={`fp-explore-status-${f.key}`}>
                      {st.label}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          FOOTER STRIP — logo + policy links
         ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-100 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-6 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <img
              src="/foodpulse-logo-round.png"
              alt=""
              width="28"
              height="28"
              className="h-7 w-7 rounded-full ring-1 ring-emerald-100"
              loading="lazy"
              decoding="async"
            />
            <span>© FoodPulse — Save food, save money, strengthen communities.</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/food-pulse/contributors" className="hover:text-emerald-700">Contributors</Link>
            <Link to="/food-pulse/corrections" className="hover:text-emerald-700">Corrections</Link>
            <Link to="/food-pulse/guides" className="hover:text-emerald-700">Guides</Link>
            <Link to="/food-pulse/about" className="hover:text-emerald-700">About</Link>
            <Link to="/install" className="hover:text-emerald-700">Get the app</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}


/* ─── Sub-components ───────────────────────────────────────────── */

// A 4-panel collage with hand-drawn-style cursive annotations sitting
// on top of the photos. The annotations use an inline SVG arrow so
// they render crisply on retina without loading a font.
function HeroCollage() {
  return (
    <div
      className="relative h-[400px] w-full sm:h-[440px] lg:h-[460px]"
      data-testid="fp-hero-collage"
      aria-hidden
    >
      {/* Main farmer photo — takes ~55% of the collage on the left */}
      <div className="absolute left-0 top-0 h-full w-[58%] overflow-hidden rounded-3xl shadow-xl ring-1 ring-emerald-100">
        <img src={IMG_FARMER} alt="" className="h-full w-full object-cover" loading="eager" />
      </div>

      {/* Top-right — vibrant markets */}
      <div className="absolute right-0 top-0 h-[36%] w-[44%] overflow-hidden rounded-2xl shadow-lg ring-1 ring-emerald-100">
        <img src={IMG_MARKET} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>

      {/* Middle-right — healthy communities */}
      <div className="absolute right-0 top-[38%] h-[26%] w-[44%] overflow-hidden rounded-2xl shadow-lg ring-1 ring-emerald-100">
        <img src={IMG_COMMUNITY} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>

      {/* Bottom-right — less waste, more good */}
      <div className="absolute right-0 bottom-0 h-[34%] w-[44%] overflow-hidden rounded-2xl shadow-lg ring-1 ring-emerald-100">
        <img src={IMG_SHARING} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>

      {/* Handwritten annotations */}
      <Annotation
        text={<>Good Food<br/>Stronger<br/>Communities</>}
        className="left-[52%] top-[4%] rotate-[-6deg]"
      />
      <Annotation
        text="Vibrant Markets"
        className="right-[46%] top-[10%] rotate-[-6deg]"
      />
      <Annotation
        text="Healthy Communities"
        className="right-[46%] top-[38%] rotate-[-4deg]"
      />
      <Annotation
        text={<>Less Waste<br/>More Good</>}
        className="right-[38%] bottom-[24%] rotate-[3deg]"
      />
      <Annotation
        text="Local Farms"
        className="left-[6%] bottom-[6%] rotate-[-3deg]"
      />
    </div>
  );
}

function Annotation({ text, className }) {
  return (
    <div
      className={`pointer-events-none absolute z-10 hidden select-none font-serif text-[13px] italic leading-tight text-emerald-800 drop-shadow-sm md:block ${className}`}
      style={{ fontFamily: '"Caveat","Kalam","Dancing Script",cursive' }}
    >
      {text}
    </div>
  );
}
